/**
 * What adding this listing would cost the buyer in shipping, expressed as a delta
 * against the box they already have.
 *
 * The listing rail shows published first-item and additional-item fees itself.
 * This module only prices the ride-along. The callout renders when the candidate
 * is combinable and the delta is cheaper than that standalone shipping line.
 *
 * The client never computes the charged box fee: the delta is the difference
 * between two server-computed ledgers — the cart as it is, and the cart with
 * this listing in it.
 */

import { cartRevalidate } from '../../util/api';

/** Rows the buyer cannot be charged for take no part in any ledger. */
const DEAD_ROW_STATES = ['sold', 'unlisted'];

// Pricing a ride-along costs two quotes, and the base one — the cart without the
// candidate — does not depend on the quantity or delivery method being chosen on the
// rail. Without a cache, browsing four listings on the same cart costs eight requests
// and every nudge of the quantity select costs two more. Caching by the rows a quote
// describes turns all of those into one request each.
//
// Deliberately local to the rail. This is an estimate next to a button; the cart page
// and checkout must both quote fresh, so neither goes through here.
const QUOTE_TTL_MS = 30 * 1000;
const MAX_CACHED_QUOTES = 8;
const quoteCache = new Map();

const destKey = destination =>
  destination?.country ? `${destination.country}:${destination.state || ''}` : '';

const quoteKey = (rows, destination) =>
  `${rows
    .map(r => `${r.listingId}:${r.qty}:${r.deliveryMethod}:${r.unitPriceAtAdd}`)
    .join('|')}|${destKey(destination)}`;

/** Test seam, and the escape hatch if a quote ever needs forcing. */
export const clearCartQuoteCache = () => {
  quoteCache.clear();
};

const cachedRevalidate = (rows, destination) => {
  const key = quoteKey(rows, destination);
  const hit = quoteCache.get(key);
  if (hit && Date.now() - hit.at < QUOTE_TTL_MS) {
    return hit.promise;
  }

  // Caching the promise rather than the response also collapses concurrent asks for the
  // same rows into one request.
  const promise =
    destination && (destination.country || destination.state)
      ? cartRevalidate(rows, { destination })
      : cartRevalidate(rows);
  quoteCache.set(key, { at: Date.now(), promise });

  // A rejection must not be remembered, or one dropped request silences the rail for the
  // rest of the TTL.
  promise.catch(() => {
    if (quoteCache.get(key)?.promise === promise) {
      quoteCache.delete(key);
    }
  });

  if (quoteCache.size > MAX_CACHED_QUOTES) {
    // Map iterates in insertion order, so the first key is the oldest.
    quoteCache.delete(quoteCache.keys().next().value);
  }

  return promise;
};

const isLiveRow = row => !!row?.listingId && !DEAD_ROW_STATES.includes(row?.state);

const sumLedgerTotals = response =>
  (response?.groups || []).reduce((sum, group) => sum + (group?.ledger?.total || 0), 0);

const groupHolding = (response, listingId) =>
  (response?.groups || []).find(group => (group?.listingIds || []).includes(listingId)) || null;

/**
 * Compare two revalidate responses to price one listing's ride-along.
 *
 * @param {{ rows: Array<Object>, groups: Array<Object> }} baseResponse cart without the candidate
 * @param {{ rows: Array<Object>, groups: Array<Object> }} withCandidateResponse cart with it
 * @param {string} listingId the candidate
 * @returns {{ delta: number, currency: string|null, combinable: boolean }} delta in integer subunits
 */
export const computeShippingDelta = (baseResponse, withCandidateResponse, listingId) => {
  const delta = sumLedgerTotals(withCandidateResponse) - sumLedgerTotals(baseResponse);

  const group = listingId ? groupHolding(withCandidateResponse, listingId) : null;
  const liveIds = new Set(
    (withCandidateResponse?.rows || []).filter(isLiveRow).map(row => row.listingId)
  );

  // Combinable means the candidate shares its box with another row that is actually
  // going to be shipped. Alone in the box — or boxed only with dead rows — it is not a
  // ride-along, and a delta there would just be a standalone quote by another name.
  const combinable =
    !!group &&
    liveIds.has(listingId) &&
    group.listingIds.some(id => id !== listingId && liveIds.has(id));

  const currency =
    group?.ledger?.currency ||
    (withCandidateResponse?.groups || []).find(g => g?.ledger?.currency)?.ledger?.currency ||
    null;

  return { delta, currency, combinable };
};

/**
 * The rail's published shipping line is the source of truth. A live cart delta
 * only renders when it would actually save versus that number.
 *
 * @param {boolean} combinable
 * @param {number} delta integer subunits from the server ledgers
 * @param {number} standaloneShipping integer subunits from listingPriceBreakdown
 * @returns {boolean}
 */
export const shouldShowCartShippingCallout = (combinable, delta, standaloneShipping) =>
  combinable === true &&
  Number.isInteger(delta) &&
  Number.isInteger(standaloneShipping) &&
  delta < standaloneShipping;

/**
 * Pick the rail callout and the money it should print.
 *
 * A negative delta means adding this listing would drop the box shipping charge
 * (free-shipping threshold). Show that as savings, not as "Adds -$X".
 *
 * @param {number} delta integer subunits from the server ledgers
 * @returns {{ messageId: string, amountSubunits: number }|null}
 */
export const resolveCartShippingDeltaCallout = delta => {
  if (!Number.isInteger(delta)) {
    return null;
  }
  if (delta < 0) {
    return {
      messageId: 'OrderPanel.cartShippingDeltaFreeShipping',
      amountSubunits: -delta,
    };
  }
  return {
    messageId: 'OrderPanel.cartShippingDelta',
    amountSubunits: delta,
  };
};

/**
 * What adding this listing would add to the buyer's shipping total, in subunits.
 *
 * A combinable ride-along uses the server delta (including $0 extra). Otherwise the
 * published one-item fee. Null means don't show a shipping line.
 *
 * @param {Object} params
 * @param {number|null|undefined} params.standaloneShipping
 * @param {boolean} [params.combinable]
 * @param {number} [params.delta]
 * @param {'idle'|'loading'|'ready'|'error'} [params.quoteStatus='idle']
 * @returns {number|null}
 */
export const resolveAddedShippingAmount = ({
  standaloneShipping,
  combinable,
  delta,
  quoteStatus = 'idle',
}) => {
  if (quoteStatus === 'loading') {
    return null;
  }
  if (combinable === true) {
    return Number.isInteger(delta) && delta >= 0 ? delta : null;
  }
  if (Number.isInteger(standaloneShipping) && standaloneShipping > 0) {
    return standaloneShipping;
  }
  return null;
};

/**
 * Ask the server what the cart costs with and without this listing.
 *
 * The candidate is removed from the base rows first, so re-adding a listing that is
 * already in the cart still measures a real difference rather than nothing at all.
 * Both requests go out together — the second is not a refinement of the first — and
 * both are served from the short-lived quote cache above where possible.
 *
 * @param {Object} params
 * @param {Array<Object>} params.rows current cart rows (minimal persisted shape)
 * @param {Object} params.candidateRow the row that would be added
 * @param {{ country?: string, state?: string }|null} [params.destination]
 * @returns {Promise<{ delta: number, currency: string|null, combinable: boolean }>}
 */
export const fetchCartShippingDelta = ({ rows, candidateRow, destination }) => {
  const listingId = candidateRow?.listingId;
  if (!listingId) {
    return Promise.reject(new Error('Missing candidate row'));
  }

  const baseRows = (Array.isArray(rows) ? rows : []).filter(row => row.listingId !== listingId);
  const withCandidateRows = [...baseRows, candidateRow];

  return Promise.all([
    cachedRevalidate(baseRows, destination),
    cachedRevalidate(withCandidateRows, destination),
  ]).then(([baseResponse, withCandidateResponse]) =>
    computeShippingDelta(baseResponse, withCandidateResponse, listingId)
  );
};
